import React from 'react';
import { Avatar, Col, Collapse, List, Row, Space, Statistic, Tag, Tooltip, Typography, Button } from 'antd';
import EditableSectionDevelopment from "./EditableSectionDevelopment";
import {AddRounded, DeleteForeverRounded, EditRounded, ScheduleRounded} from "@mui/icons-material";

const { Panel } = Collapse;
const { Text, Title, Paragraph } = Typography;

const InstructionalPlanSection = ({
    segments,
    totalMinutes,
    isEditMode,
    editingState,
    adaptedKeys,
    handleEdit,
    handleContentChange,
    handleSave,
    handleCancel,
    handleAddListItem,
    handleRemoveListItem,
    handleRemoveSegment,
    handleRemoveResource,
    showSegmentEditModal,
    showBlockLibrary,
    getResourceIcon
}) => {
    return (
        <div id="lesson-plan">
            <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
                <Col>
                    <Title level={2} style={{ margin: 0 }}>Instructional Plan</Title>
                </Col>
                <Col>
                    <Statistic
                        title="Total Time"
                        value={totalMinutes}
                        suffix="min"
                        prefix={<ScheduleRounded />}
                    />
                </Col>
            </Row>
            <Collapse defaultActiveKey={[0]} expandIconPosition="end">
                {segments.map((segment, segmentIndex) => (
                    <React.Fragment key={segment.key || segmentIndex}>
                        <Panel
                            header={<Space><Avatar style={{ backgroundColor: '#1677ff' }}>{segmentIndex + 1}</Avatar><Title level={5} style={{ margin: 0 }}>{segment.name}</Title></Space>}
                            key={segmentIndex}
                            extra={
                                <Space>
                                    <Tag color={segment.isCustomBlock ? "purple" : "blue"}>{segment.minutes} min</Tag>
                                    {isEditMode && (
                                        <>
                                            <Button
                                                type="text"
                                                shape="circle"
                                                icon={<EditRounded />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    showSegmentEditModal(segmentIndex);
                                                }}
                                            />
                                            <Button
                                                type="text"
                                                danger
                                                shape="circle"
                                                icon={<DeleteForeverRounded />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveSegment(segmentIndex);
                                                }}
                                            />
                                        </>
                                    )}
                                </Space>
                            }
                        >
                            <Paragraph><strong>Goal:</strong> {segment.goal}</Paragraph>
                            <Space orientation="vertical" style={{ width: '100%' }} size="middle">

                                {/* --- Teacher Moves --- */}
                                {segment.teacherMoves && segment.teacherMoves.length > 0 && (
                                    <>
                                        <Row justify="space-between" align="middle">
                                            <Title level={5} style={{ color: '#1677ff', margin: 0 }}>Teacher Moves</Title>
                                            {isEditMode && (
                                                <Button
                                                    type="primary"
                                                    icon={<AddRounded />}
                                                    size="small"
                                                    onClick={() => handleAddListItem(`segments.${segmentIndex}.teacherMoves`, 'New move. Click to edit.', 'Teacher Move')}
                                                >
                                                    Add Move
                                                </Button>
                                            )}
                                        </Row>
                                        <List size="small" dataSource={segment.teacherMoves} renderItem={(item, itemIndex) => (
                                            <List.Item>
                                                {(() => {
                                                    const isEditingThisOne = editingState.key === `segments.${segmentIndex}.teacherMoves.${itemIndex}`;
                                                    return (
                                                        <EditableSectionDevelopment
                                                            contentKey={`segments.${segmentIndex}.teacherMoves.${itemIndex}`}
                                                            editContent={isEditingThisOne ? editingState.content : item}
                                                            isEditing={isEditingThisOne}
                                                            isAdapted={adaptedKeys.has(`segments.${segmentIndex}.teacherMoves.${itemIndex}`)}
                                                            onEdit={isEditMode ? handleEdit : undefined}
                                                            onChange={isEditMode ? handleContentChange : undefined}
                                                            onSave={isEditMode ? handleSave : undefined}
                                                            onCancel={isEditMode ? handleCancel : undefined}
                                                            onDelete={isEditMode ? () => handleRemoveListItem(`segments.${segmentIndex}.teacherMoves`, itemIndex, item) : undefined}
                                                            deleteTooltip="Remove move"
                                                        >
                                                            <Text>{item}</Text>
                                                        </EditableSectionDevelopment>
                                                    );
                                                })()}
                                            </List.Item>
                                        )} />
                                    </>
                                )}

                                {/* --- Student Actions --- */}
                                {segment.studentActions && segment.studentActions.length > 0 && (
                                    <>
                                        <Row justify="space-between" align="middle">
                                            <Title level={5} style={{ color: '#1677ff', margin: 0 }}>Student Actions</Title>
                                            {isEditMode && (
                                                <Button
                                                    type="primary"
                                                    icon={<AddRounded />}
                                                    size="small"
                                                    onClick={() => handleAddListItem(`segments.${segmentIndex}.studentActions`, 'New action. Click to edit.', 'Student Action')}
                                                >
                                                    Add Action
                                                </Button>
                                            )}
                                        </Row>
                                        <List size="small" dataSource={segment.studentActions} renderItem={(item, itemIndex) => (
                                            <List.Item>
                                                {(() => {
                                                    const isEditingThisOne = editingState.key === `segments.${segmentIndex}.studentActions.${itemIndex}`;
                                                    return (
                                                        <EditableSectionDevelopment
                                                            contentKey={`segments.${segmentIndex}.studentActions.${itemIndex}`}
                                                            editContent={isEditingThisOne ? editingState.content : item}
                                                            isEditing={isEditingThisOne}
                                                            isAdapted={adaptedKeys.has(`segments.${segmentIndex}.studentActions.${itemIndex}`)}
                                                            onEdit={isEditMode ? handleEdit : undefined}
                                                            onChange={isEditMode ? handleContentChange : undefined}
                                                            onSave={isEditMode ? handleSave : undefined}
                                                            onCancel={isEditMode ? handleCancel : undefined}
                                                            onDelete={isEditMode ? () => handleRemoveListItem(`segments.${segmentIndex}.studentActions`, itemIndex, item) : undefined}
                                                            deleteTooltip="Remove action"
                                                        >
                                                            <Text>{item}</Text>
                                                        </EditableSectionDevelopment>
                                                    );
                                                })()}
                                            </List.Item>
                                        )} />
                                    </>
                                )}

                                {/* --- Checks for Understanding --- */}
                                {segment.checksForUnderstanding && segment.checksForUnderstanding.length > 0 && (
                                    <>
                                        <Row justify="space-between" align="middle">
                                            <Title level={5} style={{ color: '#1677ff', margin: 0 }}>Checks for Understanding</Title>
                                            {isEditMode && (
                                                <Button
                                                    type="primary"
                                                    icon={<AddRounded />}
                                                    size="small"
                                                    onClick={() => handleAddListItem(`segments.${segmentIndex}.checksForUnderstanding`, 'New check. Click to edit.', 'Check for Understanding')}
                                                >
                                                    Add Check
                                                </Button>
                                            )}
                                        </Row>
                                        <List size="small" dataSource={segment.checksForUnderstanding} renderItem={(item, itemIndex) => (
                                            <List.Item>
                                                {(() => {
                                                    const isEditingThisOne = editingState.key === `segments.${segmentIndex}.checksForUnderstanding.${itemIndex}`;
                                                    return (
                                                        <EditableSectionDevelopment
                                                            contentKey={`segments.${segmentIndex}.checksForUnderstanding.${itemIndex}`}
                                                            editContent={isEditingThisOne ? editingState.content : item}
                                                            isEditing={isEditingThisOne}
                                                            isAdapted={adaptedKeys.has(`segments.${segmentIndex}.checksForUnderstanding.${itemIndex}`)}
                                                            onEdit={isEditMode ? handleEdit : undefined}
                                                            onChange={isEditMode ? handleContentChange : undefined}
                                                            onSave={isEditMode ? handleSave : undefined}
                                                            onCancel={isEditMode ? handleCancel : undefined}
                                                            onDelete={isEditMode ? () => handleRemoveListItem(`segments.${segmentIndex}.checksForUnderstanding`, itemIndex, item) : undefined}
                                                            deleteTooltip="Remove check"
                                                        >
                                                            <Text>{item}</Text>
                                                        </EditableSectionDevelopment>
                                                    );
                                                })()}
                                            </List.Item>
                                        )} />
                                    </>
                                )}

                                {/* --- Materials --- */}
                                {segment.materials && segment.materials.length > 0 && (
                                    <>
                                        <Row justify="space-between" align="middle">
                                            <Title level={5} style={{ color: '#1677ff', marginTop: '16px', marginBottom: 0 }}>Materials</Title>
                                            {isEditMode && (
                                                <Button
                                                    type="primary"
                                                    icon={<AddRounded />}
                                                    size="small"
                                                    onClick={() => handleAddListItem(`segments.${segmentIndex}.materials`, 'New material. Click to edit.', 'Material')}
                                                >
                                                    Add Material
                                                </Button>
                                            )}
                                        </Row>
                                        <List size="small" dataSource={segment.materials} renderItem={(item, itemIndex) => (
                                            <List.Item>
                                                {(() => {
                                                    const isEditingThisOne = editingState.key === `segments.${segmentIndex}.materials.${itemIndex}`;
                                                    return (
                                                        <EditableSectionDevelopment
                                                            contentKey={`segments.${segmentIndex}.materials.${itemIndex}`}
                                                            editContent={isEditingThisOne ? editingState.content : item}
                                                            isEditing={isEditingThisOne}
                                                            isAdapted={adaptedKeys.has(`segments.${segmentIndex}.materials.${itemIndex}`)}
                                                            onEdit={isEditMode ? handleEdit : undefined}
                                                            onChange={isEditMode ? handleContentChange : undefined}
                                                            onSave={isEditMode ? handleSave : undefined}
                                                            onCancel={isEditMode ? handleCancel : undefined}
                                                            onDelete={isEditMode ? () => handleRemoveListItem(`segments.${segmentIndex}.materials`, itemIndex, item) : undefined}
                                                            deleteTooltip="Remove material"
                                                        >
                                                            <Text>{item}</Text>
                                                        </EditableSectionDevelopment>
                                                    );
                                                })()}
                                            </List.Item>
                                        )} />
                                    </>
                                )}

                                {/* --- Aligned Resources --- */}
                                {segment.textbookResources && segment.textbookResources.length > 0 && (
                                    <>
                                        <Title level={5} style={{ color: '#1677ff', marginTop: '16px' }}>Aligned Resources</Title>
                                        <List
                                            size="small"
                                            dataSource={segment.textbookResources}
                                            renderItem={(resource, resourceIndex) => (
                                                <List.Item
                                                    actions={isEditMode ? [
                                                        <Tooltip title="Remove resource">
                                                            <Button
                                                                type="text"
                                                                danger
                                                                shape="circle"
                                                                icon={<DeleteForeverRounded />}
                                                                onClick={() => handleRemoveResource(segmentIndex, resourceIndex)}
                                                            />
                                                        </Tooltip>
                                                    ] : []}
                                                >
                                                    <List.Item.Meta
                                                        avatar={getResourceIcon(resource.contentType)}
                                                        title={<a href={`/resource/${resource.documentId}`} target="_blank" rel="noopener noreferrer">{resource.displayTitle}</a>}
                                                        description={resource.contentType.replace(/_/g, ' ')}
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    </>
                                )}
                            </Space>
                        </Panel>
                        {isEditMode && (
                            <div style={{ margin: '8px 0', textAlign: 'center' }}>
                                <Button
                                    type="dashed"
                                    shape="circle"
                                    icon={<AddRounded />}
                                    title="Add instructional block here"
                                    onClick={() => showBlockLibrary(segmentIndex + 1)}
                                />
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </Collapse>
        </div>
    );
};

export default InstructionalPlanSection;
